<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { QDateProps, QTimeProps } from 'quasar'
import { QBtn, QDialog, QCard, QCardSection, QCardActions, QDate, QTime } from 'quasar'
import { parse, isValid, format } from 'date-fns'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: Date, required: true },
  dateProps: { type: Object as () => Partial<QDateProps>, default: () => ({}) },
  timeProps: { type: Object as () => Partial<QTimeProps>, default: () => ({}) }
})
const emit = defineEmits(['update:modelValue'])

const popup = ref(false)
const inputValue = ref('')
const localDate = ref(props.modelValue ? new Date(props.modelValue) : new Date())

// Date/time formats
const DISPLAY_FORMAT = 'Pp' // locale date+time
const DATE_FORMAT = 'yyyy/MM/dd'
const TIME_FORMAT = 'HH:mm'

watch(
  () => localDate.value,
  (val) => {
    inputValue.value = val ? format(val, DISPLAY_FORMAT) : ''
  },
  { immediate: true }
)

watch(
  () => props.modelValue,
  (val) => {
    if (val && val.getTime() !== localDate.value.getTime()) {
      localDate.value = new Date(val)
    }
  }
)

// Track if the user is editing
const editing = ref(false)

function tryParseDate(val: string): Date | null {
  // Try parsing as DISPLAY_FORMAT, DATE_FORMAT, TIME_FORMAT, or ISO
  let parsed = parse(val, DISPLAY_FORMAT, new Date())
  if (isValid(parsed)) return parsed
  parsed = parse(val, DATE_FORMAT, new Date())
  if (isValid(parsed)) return parsed
  parsed = parse(val, TIME_FORMAT, new Date())
  if (isValid(parsed)) return parsed
  parsed = new Date(val)
  if (isValid(parsed)) return parsed
  return null
}

function onInput(val: string | number | null) {
  if (typeof val !== 'string') return
  inputValue.value = val
  editing.value = true
}

function onBlur() {
  if (!editing.value) return
  const parsed = tryParseDate(inputValue.value)
  if (parsed) {
    localDate.value = parsed
    emit('update:modelValue', new Date(parsed))
  } else {
    // Reset to last valid value
    inputValue.value = localDate.value ? format(localDate.value, DISPLAY_FORMAT) : ''
  }
  editing.value = false
}

function onDateChange(dateStr: string) {
  const d = parse(dateStr, DATE_FORMAT, localDate.value)
  if (isValid(d)) {
    // Preserve time from localDate
    d.setHours(
      localDate.value.getHours(),
      localDate.value.getMinutes(),
      localDate.value.getSeconds(),
      0
    )
    localDate.value = d
    emit('update:modelValue', new Date(d))
  }
}

function onTimeChange(value: string | null) {
  if (!value) return
  const t = parse(value, TIME_FORMAT, localDate.value)
  if (isValid(t)) {
    // Set time on localDate
    const d = new Date(localDate.value)
    d.setHours(t.getHours(), t.getMinutes(), 0, 0)
    localDate.value = d
    emit('update:modelValue', new Date(d))
  }
}

function clear() {
  localDate.value = new Date()
  emit('update:modelValue', new Date())
}

const dateString = computed({
  get() {
    const d = localDate.value
    return d ? format(d, DATE_FORMAT) : ''
  },
  set(val: string) {
    onDateChange(val)
  }
})

const timeString = computed({
  get() {
    const d = localDate.value
    return d ? format(d, TIME_FORMAT) : ''
  },
  set(val: string) {
    onTimeChange(val)
  }
})
</script>

<template>
  <div>
    <q-input
      v-model="inputValue"
      v-bind="$attrs"
      @clear="clear"
      @update:model-value="onInput"
      @blur="onBlur"
      :type="'text'"
    >
      <template #prepend>
        <q-btn flat round dense icon="mdi-calendar-edit" @click.stop="popup = true" />
      </template>
    </q-input>
    <q-dialog v-model="popup">
      <q-card style="max-width: 95vw">
        <q-card-section>
          <div class="row wrap" style="gap: 16px">
            <q-date v-model="dateString" @update:model-value="onDateChange" v-bind="dateProps" />
            <q-time
              v-model="timeString"
              @update:model-value="onTimeChange"
              v-bind="timeProps"
              format24h
            />
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Close" color="primary" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>
